import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { formatDate, LOCALE_MAP } from 'utils/locale-formatting'
import styled from 'styled-components'

const EmptyText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px var(--vo-escuro-8);
  border: 1px solid var(--vo-cloud);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const DocIconBox = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: var(--vo-capri-50);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const DocSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
)

const DocInfo = styled.div``

const DocName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DocMeta = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`

const DownloadSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
)

const DownloadBtn = styled.a`
  height: 36px;
  background: white;
  color: var(--color-brand);
  border: 1.5px solid var(--color-brand);
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  text-decoration: none;
  flex-shrink: 0;
  &:hover { background: var(--vo-capri-50); }
`

export const PublicPolicies = ({ policies = [] }) => {
  const { t, i18n } = useTranslation('transparency')
  const locale = LOCALE_MAP[i18n.language] ?? 'en-US'

  if (!policies || policies.length === 0) {
    return <EmptyText>{t('public.emptyPolicies')}</EmptyText>
  }

  return (
    <Column>
      {policies.map((policy, index) => {
        const versionStr = policy.version ? `v${policy.version}` : ''
        const dateStr = policy.lastUpdated ? formatDate(policy.lastUpdated, locale) : ''
        const meta = [versionStr, dateStr].filter(Boolean).join(' · ')
        return (
          <Card key={policy.id || index}>
            <Left>
              <DocIconBox>
                <DocSvg />
              </DocIconBox>
              <DocInfo>
                <DocName>{policy.title}</DocName>
                {meta && <DocMeta>{meta}</DocMeta>}
              </DocInfo>
            </Left>
            <DownloadBtn
              href={policy.downloadUrl || '#'}
              target={policy.downloadUrl ? '_blank' : undefined}
              rel="noopener noreferrer"
              onClick={!policy.downloadUrl ? (e) => e.preventDefault() : undefined}
            >
              <DownloadSvg />
              {t('shared.downloadPolicy')}
            </DownloadBtn>
          </Card>
        )
      })}
    </Column>
  )
}

PublicPolicies.propTypes = {
  policies: PropTypes.array,
}
